import lodash from 'lodash'
import React, { useCallback, useMemo } from 'react'
import { useContextSelector } from 'use-context-selector'

import { FormContext } from 'components/formKit/Form'
import type { IButtonPropsType } from 'components/uiKit/Button'
import type { IIconButtonProps } from 'components/uiKit/Button/IconButton/IconButton'
import IconButton from 'components/uiKit/Button/IconButton/IconButton'

type FormIconButtonResetPropsType<
  C extends IButtonPropsType | IIconButtonProps = IIconButtonProps,
> = C & { Component?: React.ForwardRefExoticComponent<C & React.RefAttributes<HTMLButtonElement>> }

const FormIconButtonReset = <C extends IButtonPropsType | IIconButtonProps = IIconButtonProps>({
  Component,
  ...props
}: FormIconButtonResetPropsType<C>) => {
  const onChange = useContextSelector(FormContext, (state) => state.onChangeValue)
  const defaultValues = useContextSelector(FormContext, (state) => state.defaultValues)
  const values = useContextSelector(FormContext, (state) => state.values)
  const valid = useContextSelector(FormContext, (state) => state?.isValid)
  const disabled = useMemo(
    () => props.disabled || !valid || lodash.isEqual(values, defaultValues),
    [props.disabled, valid, values, defaultValues],
  )

  const handleClick = useCallback(
    async () => onChange?.({ name: '', value: defaultValues }),
    [onChange, defaultValues],
  )

  if (!Component) {
    return (
      <IconButton
        {...(props as unknown as IIconButtonProps)}
        disabled={disabled}
        onClick={handleClick}
      />
    )
  }

  return <Component {...(props as unknown as C)} disabled={disabled} onClick={handleClick} />
}

export default FormIconButtonReset
